import { useEffect, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import './styles/theme.css';
import { ApiError, api } from './api/client';
import type { Me } from './api/types';
import { APP_VERSION } from './app/version';
import { AccountPage } from './features/auth/AccountPage';
import { AdminPage } from './features/admin/AdminPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { LoginPage } from './features/auth/LoginPage';
import { ForcedPasswordChange } from './features/auth/ForcedPasswordChange';
import { SetPasswordPage } from './features/auth/SetPasswordPage';
import { SignUpPage } from './features/auth/SignUpPage';
import { ToolPage } from './features/tools/ToolPage';
import { VerifySignupPage } from './features/auth/VerifySignupPage';
import { initials } from './shared/format';
import {
  IconBuilding,
  IconChevronDown,
  IconChevronRight,
  IconGrid,
  IconMoon,
  IconSignOut,
  IconSun,
  IconUserCog,
} from './shared/icons';
import { isPublicRoute, useRouter, type Route } from './shared/router';
import { useTheme } from './shared/theme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 means "sign in", not "try again"; retrying only delays the
      // redirect and doubles the log noise.
      retry: (failureCount, error) =>
        !(error instanceof ApiError && error.isUnauthenticated) && failureCount < 2,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppRoot />
    </QueryClientProvider>
  );
}

function AppRoot() {
  const queryClientRef = useQueryClient();
  const { route, navigate } = useRouter();

  // A public route renders before anything is known about the session: someone
  // following an invitation from their inbox has no session yet, and asking
  // the API about one first would only add a redirect to the sign-in page.
  const publicRoute = isPublicRoute(route);

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: false,
    enabled: !publicRoute,
  });

  // Signing in from a public screen lands on the dashboard rather than leaving
  // a spent token in the address bar.
  const enterApp = (me: Me) => {
    queryClientRef.setQueryData(['me'], me);
    navigate({ name: 'dashboard' }, { replace: true });
  };

  const toSignIn = () => navigate({ name: 'dashboard' }, { replace: true });

  if (publicRoute) {
    if (route.name === 'set-password') {
      return (
        <SetPasswordPage
          token={route.token}
          kind={route.kind}
          onSignedIn={enterApp}
          onGoToSignIn={toSignIn}
        />
      );
    }
    if (route.name === 'sign-up') {
      return <SignUpPage onGoToSignIn={toSignIn} />;
    }
    if (route.name === 'verify-signup') {
      return (
        <VerifySignupPage
          token={route.token}
          onSignedIn={enterApp}
          onGoToSignIn={toSignIn}
          onGoToSignUp={() => navigate({ name: 'sign-up' }, { replace: true })}
        />
      );
    }
    return <ForgotPasswordPage onGoToSignIn={toSignIn} />;
  }

  if (meQuery.isPending) {
    return <div className="page-state">Loading…</div>;
  }

  const unauthenticated =
    meQuery.isError && meQuery.error instanceof ApiError && meQuery.error.isUnauthenticated;

  if (unauthenticated || (meQuery.isError && !meQuery.data)) {
    if (!unauthenticated) {
      return (
        <div className="page-state">
          <p>The platform could not be reached. Check your connection and reload.</p>
        </div>
      );
    }
    return (
      <LoginPage
        // The login response is the same payload as /me, so seeding the cache
        // here avoids a second request and a flash of "Loading…".
        onSignedIn={(me) => queryClientRef.setQueryData(['me'], me)}
        onForgotPassword={() => navigate({ name: 'forgot-password' })}
        onSignUp={() => navigate({ name: 'sign-up' })}
      />
    );
  }

  const me = meQuery.data as Me;

  // An account holding a temporary password gets nothing else until it is
  // replaced. The server enforces this too; this screen is what the person sees.
  if (me.user.must_change_password) {
    return (
      <ForcedPasswordChange
        onChanged={() => queryClientRef.invalidateQueries({ queryKey: ['me'] })}
        onSignOut={async () => {
          try {
            await api.logout();
          } finally {
            queryClientRef.clear();
            window.location.reload();
          }
        }}
      />
    );
  }

  return <AppShell me={me} route={route} navigate={navigate} />;
}

function AppShell({
  me,
  route,
  navigate,
}: {
  me: Me;
  route: Route;
  navigate: (route: Route, options?: { replace?: boolean }) => void;
}) {
  const queryClientRef = useQueryClient();
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // One tier: EJOT's own operators. Nobody administers a company here.
  const isAdmin = me.user.is_platform_admin;

  // Somebody who lands on a screen they cannot use (a bookmark, a shared link)
  // is sent back rather than shown an empty one.
  useEffect(() => {
    if (route.name === 'admin' && !isAdmin) {
      navigate({ name: 'dashboard' }, { replace: true });
    }
  }, [route, isAdmin, navigate]);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const signOut = async () => {
    try {
      await api.logout();
    } finally {
      // Whatever the server said, this browser is done with the session.
      queryClientRef.clear();
      window.history.replaceState(null, '', '/');
      window.location.reload();
    }
  };

  const crumb = crumbFor(route, me);

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header-left">
          <div className="app-logo">
            <img src="/brand/ejot-logo.png" alt="EJOT" height={14} />
          </div>
          <button className="app-title" onClick={() => navigate({ name: 'dashboard' })}>
            Engineering Platform
          </button>
          <span className="app-version">v{APP_VERSION}</span>
          {crumb && (
            <>
              <span className="app-crumb-sep">
                <IconChevronRight size={12} />
              </span>
              <span className="app-crumb">{crumb}</span>
            </>
          )}
        </div>

        <div className="app-header-right">
          {me.user.company && (
            <span className="hdr-org">
              <IconBuilding size={12} />
              {me.user.company}
            </span>
          )}

          <button
            className="hdr-btn hdr-btn-icon"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            title={theme === 'dark' ? 'Light theme' : 'Dark theme'}
          >
            {theme === 'dark' ? <IconSun size={13} /> : <IconMoon size={13} />}
          </button>

          <div className="account-menu-wrap" ref={menuRef}>
            <button
              className="hdr-account"
              onClick={() => setMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <span className="hdr-avatar">{initials(me.user.display_name, me.user.email)}</span>
              <span className="hdr-account-text">
                <span className="hdr-account-name">{me.user.display_name}</span>
                <span className="hdr-account-role">
                  {me.user.is_platform_admin ? 'Administrator' : 'Engineer'}
                </span>
              </span>
              <IconChevronDown size={11} />
            </button>

            {menuOpen && (
              <div className="account-menu" role="menu">
                <button
                  className="account-menu-item"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate({ name: 'account' });
                  }}
                >
                  <IconUserCog size={14} />
                  Account &amp; password
                </button>
                <div className="account-menu-sep" />
                <div className="account-menu-meta">{me.user.email}</div>
                <button className="account-menu-item is-danger" role="menuitem" onClick={signOut}>
                  <IconSignOut size={14} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="workspace">
        <nav className="panel nav-panel" aria-label="Main">
          <div className="panel-hdr">
            <span>Navigation</span>
          </div>

          <div className="nav-list">
            <button
              className={`nav-item${route.name === 'dashboard' || route.name === 'tool' ? ' is-active' : ''}`}
              onClick={() => navigate({ name: 'dashboard' })}
            >
              <IconGrid size={15} />
              Tools
              <span className="nav-item-count">{me.tools.filter((t) => t.launchable).length}</span>
            </button>

            {isAdmin && (
              <>
                <div className="nav-divider">
                  <span>Administration</span>
                </div>
                <button
                  className={`nav-item${route.name === 'admin' ? ' is-active' : ''}`}
                  onClick={() => navigate({ name: 'admin' })}
                >
                  <IconUserCog size={15} />
                  Users &amp; access
                </button>
              </>
            )}
          </div>
        </nav>

        <main className="workspace-column">
          <RouteView route={route} me={me} navigate={navigate} />
        </main>
      </div>
    </div>
  );
}

function RouteView({
  route,
  me,
  navigate,
}: {
  route: Route;
  me: Me;
  navigate: (route: Route, options?: { replace?: boolean }) => void;
}) {
  switch (route.name) {
    case 'dashboard':
      return <DashboardPage me={me} navigate={navigate} />;
    case 'tool':
      return <ToolPage toolKey={route.key} navigate={navigate} />;
    case 'admin':
      return me.user.is_platform_admin ? <AdminPage currentUserId={me.user.id} /> : null;
    case 'account':
      return <AccountPage me={me} />;
    case 'set-password':
    case 'forgot-password':
    case 'sign-up':
    case 'verify-signup':
      // Handled by AppRoot before the shell renders; a signed-in user who
      // lands here has nothing to do on these screens.
      return null;
    case 'not-found':
      return (
        <div className="page-state">
          <p>There is nothing at {route.path}.</p>
          <button className="btn" onClick={() => navigate({ name: 'dashboard' })}>
            Back to tools
          </button>
        </div>
      );
  }
}

function crumbFor(route: Route, me: Me): string | null {
  switch (route.name) {
    case 'tool':
      return me.tools.find((t) => t.key === route.key)?.name ?? route.key;
    case 'admin':
      return 'Users & access';
    case 'account':
      return 'Account';
    case 'set-password':
    case 'forgot-password':
      return null;
    default:
      return null;
  }
}
